import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { useLineItemCategories } from '@/hooks/useConfigurableLists';
import { cn } from '@/lib/utils';
import { EditableItemRow, type EditableItem } from './EditableItemRow';

interface EditableItemListProps {
  title: string;
  items: EditableItem[];
  isDisclaimer?: boolean;
  isSaving?: boolean;
  showReusableToggle?: boolean;
  onMoveItem?: (id: string) => void;
  moveLabel?: string;
  onAdd: (item: { category: string; description: string }) => Promise<void>;
  onEdit: (item: EditableItem) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onReorder: (items: EditableItem[]) => Promise<void>;
}

export function EditableItemList({
  title,
  items,
  isDisclaimer = false,
  isSaving = false,
  showReusableToggle = false,
  onMoveItem,
  moveLabel,
  onAdd,
  onEdit,
  onDelete,
  onReorder,
}: EditableItemListProps) {
  const { data: lineItemCategories } = useLineItemCategories();
  const categoryOptions = lineItemCategories.map((c) => ({ value: c, label: c }));

  const [isAdding, setIsAdding] = useState(false);
  const [newCategory, setNewCategory] = useState<string>('');
  const [newDescription, setNewDescription] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  // Optimistic local order so rows don't snap back while the API call is in flight
  const [localItems, setLocalItems] = useState(items);
  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = localItems.findIndex((item) => item.id === active.id);
      const newIndex = localItems.findIndex((item) => item.id === over.id);
      const reordered = arrayMove(localItems, oldIndex, newIndex);
      setLocalItems(reordered);
      onReorder(reordered);
    }
  };

  const activeItem = activeId ? localItems.find((item) => item.id === activeId) : null;

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewCategory(lineItemCategories[0] || '');
    setNewDescription('');
  };

  const handleAdd = async () => {
    if (!newDescription.trim()) return;

    await onAdd({
      category: isDisclaimer ? 'Disclaimer' : newCategory,
      description: newDescription,
    });

    handleCancelAdd();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h4>
        <Button
          size="sm"
          variant={isDisclaimer ? 'outline' : 'default'}
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add {isDisclaimer ? 'Disclaimer' : 'Line Item'}
        </Button>
      </div>

      <div className="space-y-1">
        {isAdding && (
          <div
            className={cn(
              'border rounded-lg p-4 mb-2',
              isDisclaimer
                ? 'border-amber-200 bg-amber-50/50'
                : 'border-blue-200 bg-blue-50/50'
            )}
          >
            <div className="space-y-3">
              {!isDisclaimer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <Combobox
                    value={newCategory}
                    onValueChange={setNewCategory}
                    options={categoryOptions}
                    placeholder="Select category"
                    searchPlaceholder="Search categories..."
                    emptyText="No category found."
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isDisclaimer ? 'Disclaimer Text' : 'Description'}
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  rows={isDisclaimer ? 3 : 2}
                  placeholder={isDisclaimer ? 'Enter the disclaimer text' : 'Description of this line item'}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={handleCancelAdd}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleAdd} disabled={isSaving}>
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={localItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            {localItems.map((item) => (
              <EditableItemRow
                key={item.id}
                item={item}
                isDisclaimer={isDisclaimer}
                isSaving={isSaving}
                showReusableToggle={showReusableToggle}
                onMove={onMoveItem}
                moveLabel={moveLabel}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeItem ? (
              <div className="border-l-4 border-blue-500 bg-blue-50 px-3 py-2 shadow-lg rounded-lg opacity-90">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block text-xs px-2 py-0.5 rounded-full border ${
                        isDisclaimer
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-white text-gray-700 border-gray-200'
                      }`}
                    >
                      {isDisclaimer ? 'Disclaimer' : activeItem.category}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mt-1 truncate">{activeItem.description}</p>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {localItems.length === 0 && !isAdding && (
          <p className="text-gray-500 text-sm text-center py-6 border border-dashed border-gray-200 rounded-lg">
            {isDisclaimer
              ? 'No disclaimers in this template.'
              : 'No line items in this template. Add one to get started.'}
          </p>
        )}
      </div>
    </div>
  );
}
